import { Injectable, inject } from '@angular/core';
import Pusher from 'pusher-js';
import { Config } from '../config/config.service';
import { interval } from "rxjs";

import {AppState, selectMicrositeAuthToken} from '../../NGRX/core.state';
import { Store } from '@ngrx/store';
import { setWebsocketBalance, setWebsocketEvent } from '../../NGRX/coreWebsockets/coreWebsockets.actions';
import {take} from "rxjs/operators";
import { CB_CORE_ENV } from '../../environment/cb-core-env.token';


@Injectable({
  providedIn: 'root'
})
export class SecureWebsocketService {
  public socket: any;
  private channel: any = {};
  private apiUrl: any;
  private maxRetryTimes = 5;
  private retryCount = 0;

  private env = inject(CB_CORE_ENV);

  constructor(
    private _config: Config,
    private store: Store<AppState>
  ) {
    this.apiUrl = this._config.CONFIG.apiUrl;
  }

  connect(headers: any): void {
    if (!this.isConnected()) {
      const parseHeaders: any = {};
      parseHeaders['uid'] = headers.uid;
      if (this._config.CONFIG.fallbackSecureWSnonCB) {
        parseHeaders['access-token'] = 'disabled';
        parseHeaders['client'] = 'disabled';
        parseHeaders['expiry'] = 'disabled';
        const authToken = this.store.selectSignal(selectMicrositeAuthToken)();
        if(this.env.allEndpointsWithCredentials && authToken) {
          if (authToken.tokens.access_token) {
            parseHeaders['authorization'] = authToken.tokens.access_token;
            parseHeaders['client'] = 'disabled';
            parseHeaders['expiry'] = 'disabled';
          }
        }
      } else {
        parseHeaders['access-token'] = headers['access-token'];
        parseHeaders['client'] = headers.client;
        parseHeaders['expiry'] = headers.expiry;
      }
      this.socket = new Pusher(this._config.CONFIG.websocketsKey, {
        wsHost: this._config.CONFIG.websocketsUrl,
        cluster: this._config.CONFIG.websocketCluster,
        disableStats: true,
        enabledTransports: ['ws'],
        authEndpoint: `${this.apiUrl}/pusher_auths`,
        auth: {
          headers: parseHeaders
        }
      });

      this.subscribeChannel(`private-user-${parseHeaders['uid']}`);
      this.bindAllChannelEvents(`private-user-${parseHeaders['uid']}`);
    }
  }

  subscribe(channel: string) {
    try {
      this.socket.subscribe(channel);
    } catch (e) {
      console.info('Unable to connect to: ' + channel);
    }
  }

  isSubscribedToChannel(channel: string) {
    return this.socket.getChannel(channel); // will return null if not subscribed to it
  }

  isConnected(): boolean {
    if (typeof this.socket !== 'undefined') {
      if (this.socket.connection.state === 'connected' || this.socket.connection.state === 'connecting') {
        return true;
      }
    }
    return false;
  }

  getChannel(channel: string): any {
    try {
      return this.socket.getChannel(channel);
    } catch (e) {
      return null;
    }
  }

  closeConnection() {
    if (this.isConnected()) {
      this.socket.disconnect();
      this.channel = {};
    }
  }

  subscribeChannel(channelName: string) {
    if(!this.channel || !this.socket) return;
    this.channel = this.socket.subscribe(channelName);
  }

  unSubscribeChannel(channelName: string) {
    try {
      if (typeof this.channel !== 'undefined' && this.isConnected()) {
        this.socket.unsubscribe(channelName);
        this.channel = undefined;
      }
    } catch (e: any) {
      console.info('can\'t unsubscribe from secure commentary channel')
    }
  }

  bindAllChannelEvents(channelName: string) {
    // we will retry 5 times the bind, because in some cases the WS is not present
    // if still fails, we stop retrying
    if (!this.socket) {
      if (this.retryCount > this.maxRetryTimes) {
        console.info('Secure WS not connected');
        return;
      }

      this.retryCount++;
      interval(500).pipe(take(1)).subscribe(() => {
        this.bindAllChannelEvents(channelName);
      })
    } else {
      this.socket.allChannels().forEach((channel: any) => {
        if (channel.subscribed === false) {
          channel.bind_global((event: any, data: any) => {

            try {
              if (channel.name.includes('private-syndicate-')) {
                this.handleCommentaryEvents(event, data);
              }
            } catch (e) {
              console.info('Commentary WS failed', e);
            }

            try {
              if (channel.name.includes('private-user-')) {
                this.handleUserEvents(event, data);
                this.handleGTEvents(event, data);
                this.handleNotificationEvents(event, data);
                this.handleUserTerminationEvent(event, data);
                this.handleBbEntryEvents(event, data);

                // TODO: this one here
                if (event === 'balance_update') {
                  if (data) {
                    this.store.dispatch(setWebsocketBalance({
                      balance: data
                    }));
                  }
                }
              }
            } catch (e) {
              console.info('Private WS failed', e);
            }

            try {
              if (channel.name.includes('private-group-ticket')) {
                this.handleGTEvents(event, data);
              }
            } catch (e) {
              console.info('GT WS failed', e);
            }
          })
        }
      });
    }
  }

  handleCommentaryEvents(event: string, data: any): void {
    const basicEvents: Record<string, string> = {
      'social_create': 'SOCIAL_CREATE'
    }

    if (!basicEvents[event] !== undefined) return;

    // basically we should dispatch an event to show the refresh button
    if (basicEvents[event] === 'SOCIAL_CREATE')
      this.store.dispatch(setWebsocketEvent({
        event: basicEvents[event],
        data: data?.data
      }));


  }

  handleUserEvents(event: string, data: any): void {
    const allowCashOuts = this._config.CONFIG.allowCashOuts;
    const basicEvents: Record<string, string> = {
      'new_offer': 'USER_NEW_OFFER',
      'remove_offer': 'USER_REMOVE_OFFER'
    }

    if (!basicEvents[event] || !allowCashOuts) return;
    this.store.dispatch(setWebsocketEvent({
      event: basicEvents[event],
      data: data?.data ?? data
    }));
  }

  /**
   * Forwards Bet Builder entry events from the private-user channel to the
   * global NgRx store via setWebsocketEvent.  The original event name is
   * preserved so BetBuilder effects can filter and dispatch specific actions.
   *
   * Handled events:
   *   entry.selection_resolved     — one or more legs resolved (won/lost)
   *   entry.status_changed         — entry status transition (won/lost/voided)
   *   entry.counters_changed       — abandoned/remaining counts updated
   *   entry.prop_updated           — prop display data changed
   *   entry.consolation_eliminated — entry removed from consolation pool
   *   entry.consolation_resolved   — consolation result finalised
   *   entry.game_started           — one of the entry's games has kicked off
   *   entry.game_unstarted         — a previously started game was rolled back
   */
  handleBbEntryEvents(event: string, data: any): void {
    const BB_ENTRY_EVENTS = [
      'entry.selection_resolved',
      'entry.status_changed',
      'entry.counters_changed',
      'entry.prop_updated',
      'entry.consolation_eliminated',
      'entry.consolation_resolved',
      'entry.game_started',
      'entry.game_unstarted',
    ];
    if (!BB_ENTRY_EVENTS.includes(event)) return;
    this.store.dispatch(setWebsocketEvent({
      event,
      data: data?.data ?? data,
    }));
  }

  handleGTEvents(event: any, data: any): void {
    /* INFO UPDATE */
    const basicEvents: Record<string, string> = {
      'group_ticket_info_update': 'INFO_UPDATE',
      'new_vote': 'VOTES_UPDATE',
      'syndicate-creation': 'SYNDICATE_CREATED'
    }

    if (!basicEvents[event]) return;

    // basically we should dispatch an event to show the refresh button
    if (basicEvents[event] === 'INFO_UPDATE' ||
      basicEvents[event] === 'VOTES_UPDATE' ||
      basicEvents[event] === 'SYNDICATE_CREATED'
    )
      this.store.dispatch(setWebsocketEvent({
        event: basicEvents[event],
        data: data
      }));
  }

  handleNotificationEvents(event: any, data: any): void {
    const basicEvents: Record<string, string> = {
      'push_notification': 'USER_NEW_NOTIFICATION'
    }

    if (!basicEvents[event]) return;

    if (basicEvents[event] === 'USER_NEW_NOTIFICATION') {
      this.store.dispatch(setWebsocketEvent({
        event: basicEvents[event],
        data: data?.data ?? data
      }));
    }
  }

  handleUserTerminationEvent(event: any, data: any): void {
    const basicEvents: Record<string, string> = {
      'session_terminated': 'SESSION_TERMINATION'
    }
    if (!basicEvents[event]) return;
    if (basicEvents[event] === 'SESSION_TERMINATION') {
      this.store.dispatch(setWebsocketEvent({
        event: basicEvents[event],
        data: data?.data ?? data
      }))
    }
  }
}
